var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import * as moment from "moment";
import 'moment-timezone';
import DateManipulator from '@/utils/DateManipulator';
import { EServiceType } from '@/models/interfaces/Orders/EServiceType';
import { LOCATION, ACCOUNT_CHECKOUT, ORDER } from '@/stores/defs';
import { mapGetters, mapActions } from 'vuex';
import { findNextAvailableTime } from "@/utils/helper.functions";
var timeMixin = {
    data: function () {
        return {
            moment: moment
        };
    },
    computed: __assign({}, mapGetters({
        location: LOCATION.GET_LOCATION,
        orderTime: ACCOUNT_CHECKOUT.GET_ORDER_TIME,
        serviceType: ACCOUNT_CHECKOUT.GET_SERVICE_TYPE
    }), { minASAPTime: function () {
            // @ts-ignore
            var currentTime = moment().tz(this.location.timeZone);
            if (this.location && currentTime.isBefore(this.location.locationHours[0].startTime)) {
                return this.location.locationHours[0].startTime;
            }
            else {
                return DateManipulator.roundMomentTo15Min(currentTime);
            }
        },
        nextAvailableTime: function () {
            var nextAvailableTime = findNextAvailableTime(this.location);
            // if not today or before store open, add promise time
            if (!DateManipulator.isMomentToday(nextAvailableTime) ||
                nextAvailableTime.isSameOrBefore(this.location.locationHours[0].startTime)) {
                nextAvailableTime.add(this.calculatedPromiseTime, 'minutes');
            }
            return nextAvailableTime;
        },
        calculatedPromiseTime: function () {
            return (this.serviceType === EServiceType.SERVICE_TYPE_DELIVERY) ? this.location.delivery.promiseTime : this.location.pickupPromiseTime;
        },
        displayTimeAsText: function () {
            // Scheduled time should always be set
            var orderTime = this.orderTime;
            var scheduledTime = moment(orderTime);
            if (!scheduledTime) {
                return '-';
            }
            if (scheduledTime.isBefore(moment())) {
                scheduledTime = this.minASAPTime;
            }
            var today = moment().clone().set('hour', 0);
            var dayIndex = scheduledTime.diff(today, 'days');
            var day = this.getFormattedDayFromIndex(dayIndex);
            var hour = scheduledTime.format('hh');
            var minutes = scheduledTime.format('mm');
            var meridiem = scheduledTime.format('A');
            return day + " at " + hour + ":" + minutes + " " + meridiem;
        } }),
    methods: __assign({}, mapActions({
        setDelayTime: ORDER.SET_DELAY_TIME,
        setDelayType: ORDER.SET_DELAY_TYPE,
        setOrderTime: ACCOUNT_CHECKOUT.SET_ORDER_TIME,
    }), { getTimeWindow: function (serviceType) {
            switch (serviceType) {
                case EServiceType.SERVICE_TYPE_DELIVERY:
                    return 10;
                case EServiceType.SERVICE_TYPE_TAKEOUT:
                    return 5;
                default:
                    return 5;
            }
        },
        getTimeToAdd: function () {
            if (!this.orderTime) {
                return 0;
            }
            var daysHours = this.location.getDaysHours(this.orderTime);
            if (this.orderTime.isBefore(daysHours.startTime)) {
                return 0;
            }
            else {
                return this.calculatedPromiseTime;
            }
        },
        displayDateRange: function (time, format) {
            if (format === void 0) { format = "hh:mma"; }
            var timeToRange = moment(time);
            var timeToAdd = this.getTimeToAdd();
            if (!this.isTimeInPromiseRange(time) && !DateManipulator.isTimeASAP(time)) {
                timeToAdd = 0;
            }
            return timeToRange.add(timeToAdd, "minutes").format(format) + " - " + moment(timeToRange).add(this.getTimeWindow(this.serviceType), "minutes").format(format);
        },
        isTimeInPromiseRange: function (value) {
            if (DateManipulator.isMomentToday(value)) {
                var promiseTime = this.getTimeToAdd();
                // show times if current time is before store open
                if (moment().isBefore(this.location.locationHours[0].startTime) &&
                    value.isBetween(this.location.locationHours[0].startTime, moment(this.location.locationHours[0].startTime).add(promiseTime, 'minutes'), null, '[]')) {
                    return false;
                }
                else if (!DateManipulator.isTimeASAP(value) && value.isBefore(moment(this.minASAPTime).add(promiseTime, 'minutes'))) {
                    // filter out times that fall in promise time range
                    return true;
                }
                else {
                    return false;
                }
            }
            else {
                return false;
            }
        },
        getAvailableMinutes: function (day, hour) {
            // after now and before store closing
            var now = moment();
            var checkDay = moment().add(+day, 'day');
            var locationHours = this.location.getDaysHours(checkDay);
            var startTime = moment(locationHours.startTime);
            var checkTime = moment(startTime);
            if (checkTime.isBefore(now)) {
                checkTime = now;
            }
            var endTime = moment(locationHours.endTime);
            var boundingTime = moment(endTime);
            var startMinutes = +this.minutesWithInterval(checkTime, 15);
            if (checkTime.hour() > now.hour()) {
                startMinutes = checkTime.minute();
            }
            var endMinutes = +this.minutesWithInterval(boundingTime, 15);
            if (endMinutes === 0) { // Roll around, should not end at 0
                endMinutes = 59;
            }
            var availableMinutes = [];
            for (var time = startMinutes; time < endMinutes; time += 15) {
                var now_1 = moment();
                var displayMinutes = moment(now_1.minutes(time)).format('mm');
                availableMinutes.push({
                    name: displayMinutes,
                    value: time,
                });
            }
            return availableMinutes;
        },
        getAvailableHours: function (day) {
            var now = moment();
            var checkDay = moment().add(+day, 'day');
            var locationHours = this.location.getDaysHours(checkDay);
            var boundingTime = moment(locationHours.endTimeISO);
            var checkTime = moment(locationHours.startTimeISO);
            if (checkTime.isBefore(now)) {
                checkTime = now;
            }
            var startHour = checkTime.hour();
            var endHour = boundingTime.hour();
            var availableHours = [];
            for (var time = startHour; time < endHour; time++) {
                var displayTime = moment().hour(time).format('hh');
                availableHours.push({
                    name: displayTime,
                    value: time,
                });
            }
            return availableHours;
        },
        getAvailableMeridiem: function () {
            return [
                {
                    name: 'AM',
                    value: 'AM',
                },
                {
                    name: 'PM',
                    value: 'PM',
                },
            ];
        },
        getDays: function () {
            var availableDays = this.location.getAvailableDays();
            return availableDays;
        },
        getDay: function (value) {
            return moment().add(+value, 'day');
        },
        getHour: function (value) {
            return moment().hour(value).format('hh');
        },
        getMinute: function (value) {
            return moment().minute(value).format('mm');
        },
        getNow: function () {
            return moment();
        },
        // TODO replace moment with dayjs (needs to be immutable)
        // moment keeps mutating stored values
        getTimeDeltaInSeconds: function (scheduledTime) {
            var delta = scheduledTime.unix() - moment().unix();
            return delta;
        },
        getFormattedDayFromIndex: function (value) {
            var index = +value;
            var day = moment().add(+index, 'day');
            if (index === 0) {
                return 'today';
            }
            else if (index === 1) {
                return 'tomorrow';
            }
            else if (index > 1 && index < 7) {
                return day.format('dddd');
            }
            else {
                return day.format('MMMM Do');
            }
        },
        isOrderTimeBeforeNow: function () {
            var now = moment().clone();
            var orderTime = moment(this.orderTime);
            return orderTime.isBefore(now);
        },
        currentMinutesWithInterval: function (interval) {
            var now = moment();
            return this.minutesWithInterval(now, interval);
        },
        minutesWithInterval: function (time, interval) {
            var minutes = DateManipulator.roundMinutesToInterval(time, interval);
            var result = minutes.format('mm');
            return result;
        },
        currentHour: function () {
            var now = moment();
            return now.format('hh');
        },
        currentMeridiem: function () {
            var now = moment();
            return now.format('A');
        },
        getMeridiem: function (hour) {
            var time = moment().set('hour', +hour);
            return time.format('A');
        },
        getScheduledTime: function (_a) {
            var day = _a.day, hour = _a.hour, minutes = _a.minutes, meridiem = _a.meridiem;
            var addHours = (meridiem === 'PM') ? 12 : 0;
            var useHour = +hour + addHours;
            var time = moment()
                .add(+day, 'day')
                .set('hour', useHour)
                .set('minute', minutes);
            return moment(time);
        } })
};
export default timeMixin;



//////////////////
// WEBPACK FOOTER
// ./src/components/mixins/timeMixin.ts
// module id = W3gY
// module chunks = 18